/**
 * "A new version of Slimez is ready — Reload" (W032): a tab left open across a deploy keeps
 * running the old client, which the new server may not understand (an unknown class, a changed
 * message). One app-level module for the front page, Slime Wars and the Preserve:
 *
 * - The client's build (`SLIMEZ_BUILD_VERSION` / `SLIMEZ_BUILD_COMMIT`, baked in by Vite's
 *   `define`) is compared with the server's, which `/health` reports (`<endpoint>/health`: in
 *   production `https://<page host>/server/health`, same origin).
 * - Checked at app start, every `VERSION_CHECK_INTERVAL_MS`, when the tab becomes visible again
 *   and right before a room is created or joined (`beforeConnect`).
 * - A mismatch shows a non-blocking banner; before a create / join it blocks (old client + new
 *   server is not supported) and the connect is refused. While a match runs (`holdPrompt`) the
 *   banner waits for its end: it never interrupts a turn.
 * - `dev` / `unknown` builds (either side) never prompt; a failed check changes nothing (the
 *   connect itself then reports the server unreachable).
 * - W032-R m2: "Reload" remembers the server build it reloads for (`sessionStorage`). When the
 *   page comes back still mismatched against that same server build, the web image was not
 *   updated (a server-only hotfix, one image loaded): reloading cannot help, so the tab neither
 *   blocks nor prompts for that build.
 */

export interface BuildId {
  readonly version: string;
  readonly commit: string;
}

declare const __SLIMEZ_BUILD_VERSION__: string | undefined;
declare const __SLIMEZ_BUILD_COMMIT__: string | undefined;

/** This bundle's build (`dev` / `unknown` outside a release build). */
export const CLIENT_BUILD: BuildId = Object.freeze({
  version: typeof __SLIMEZ_BUILD_VERSION__ === 'string' ? __SLIMEZ_BUILD_VERSION__ : 'dev',
  commit: typeof __SLIMEZ_BUILD_COMMIT__ === 'string' ? __SLIMEZ_BUILD_COMMIT__ : 'unknown',
});

/** TUNING. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;
/** A check before a create / join waits at most this long (then the connect goes ahead). */
export const VERSION_CHECK_TIMEOUT_MS = 2500;

/** A release build: both labels set, neither the `dev` / `unknown` default. */
export function isReleaseBuild(build: BuildId): boolean {
  const v = build.version.trim();
  const c = build.commit.trim();
  return v !== '' && c !== '' && v !== 'dev' && c !== 'unknown';
}

/** The client must reload: both are release builds and they differ. */
export function buildsDiffer(client: BuildId, server: BuildId): boolean {
  return (
    isReleaseBuild(client) &&
    isReleaseBuild(server) &&
    (client.version !== server.version || client.commit !== server.commit)
  );
}

/** `<endpoint>/health` over HTTP(S) for a `ws(s)://` game endpoint. */
export function healthUrlOf(endpoint: string): string {
  const http = endpoint.replace(/^ws(s?):\/\//i, 'http$1://').replace(/\/+$/, '');
  return `${http}/health`;
}

/** Reads `{ version, commit }` from a `/health` body; `null` when malformed. */
export function pickBuild(body: unknown): BuildId | null {
  if (typeof body !== 'object' || body === null) return null;
  const { version, commit } = body as Record<string, unknown>;
  if (typeof version !== 'string' || typeof commit !== 'string') return null;
  return { version: version.slice(0, 64), commit: commit.slice(0, 64) };
}

export interface VersionPromptState {
  /** The server runs another release than this client. */
  readonly stale: boolean;
  /** A create / join was refused for it: the prompt blocks the page. */
  readonly blocking: boolean;
  /** A match is running: the (non-blocking) banner waits for its end. */
  readonly held: boolean;
}

export interface VersionWatch {
  getSnapshot(): VersionPromptState;
  subscribe(listener: () => void): () => void;
  /** Fetches the server's build now (one request at a time). */
  check(): Promise<void>;
  /**
   * Before a create / join: a fresh check (bounded by `VERSION_CHECK_TIMEOUT_MS`). `false` when
   * the server runs another release — the prompt now blocks and the caller must not connect.
   */
  beforeConnect(): Promise<boolean>;
  /** A match is running (the banner waits); returns the release. Nested holds count. */
  holdPrompt(): () => void;
  /** App start: the first check, the interval and the visibility listener; returns the stop. */
  start(): () => void;
  /** "Reload" was pressed: remembers the server build it reloads for (W032-R m2). */
  markReloading(): void;
}

export interface VersionWatchOptions {
  readonly client?: BuildId;
  /** The server's build, `null` when unknown / unreachable. */
  readonly fetchServer?: () => Promise<BuildId | null>;
  readonly intervalMs?: number;
  readonly timeoutMs?: number;
  readonly document?: Pick<
    Document,
    'visibilityState' | 'addEventListener' | 'removeEventListener'
  >;
  readonly setInterval?: (fn: () => void, ms: number) => unknown;
  readonly clearInterval?: (id: unknown) => void;
  /** Where "Reload" remembers the server build (default `sessionStorage`; null: nowhere). */
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

/** The `sessionStorage` key of the server build a "Reload" was for. */
export const RELOADED_FOR_KEY = 'slimez.reloadedFor';

const buildKey = (b: BuildId) => `${b.version}@${b.commit}`;

function sessionStore(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

async function fetchServerBuild(): Promise<BuildId | null> {
  if (typeof fetch !== 'function') return null;
  try {
    // Loaded on first use (release builds only): the front page's own modules stay game-free.
    const { resolveServerEndpoint } = await import('../game/network/endpoint');
    const response = await fetch(healthUrlOf(resolveServerEndpoint()), {
      cache: 'no-store',
      credentials: 'omit',
      // A hanging `/health` must not hold every create / join for the full wait (W032-R).
      ...(typeof AbortSignal.timeout === 'function'
        ? { signal: AbortSignal.timeout(VERSION_CHECK_TIMEOUT_MS) }
        : {}),
    });
    // A draining server answers 503 with the same body.
    return pickBuild(await response.json());
  } catch {
    return null;
  }
}

export function createVersionWatch(options: VersionWatchOptions = {}): VersionWatch {
  const client = options.client ?? CLIENT_BUILD;
  const fetchServer = options.fetchServer ?? fetchServerBuild;
  const intervalMs = options.intervalMs ?? VERSION_CHECK_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? VERSION_CHECK_TIMEOUT_MS;
  const doc = options.document ?? (typeof document === 'undefined' ? undefined : document);
  const every = options.setInterval ?? ((fn, ms) => setInterval(fn, ms));
  const stopEvery =
    options.clearInterval ?? ((id) => clearInterval(id as ReturnType<typeof setInterval>));
  const enabled = isReleaseBuild(client);
  const storage = options.storage === undefined ? sessionStore() : options.storage;
  let lastServer: BuildId | null = null;
  const reloadedFor = (): string | null => {
    try {
      return storage?.getItem(RELOADED_FOR_KEY) ?? null;
    } catch {
      return null;
    }
  };

  const listeners = new Set<() => void>();
  let state: VersionPromptState = { stale: false, blocking: false, held: false };
  let holds = 0;
  let inFlight: Promise<void> | null = null;

  const set = (patch: Partial<VersionPromptState>) => {
    const next = { ...state, ...patch };
    if (next.stale === state.stale && next.blocking === state.blocking && next.held === state.held)
      return;
    state = next;
    for (const listener of [...listeners]) listener();
  };

  const check = (): Promise<void> => {
    if (!enabled) return Promise.resolve();
    inFlight ??= fetchServer()
      .then((server) => {
        // Unknown (unreachable, malformed): keep what we know.
        if (server === null) return;
        lastServer = server;
        // Reloaded for this very server build and still mismatched: reloading cannot help.
        const futile = reloadedFor() === buildKey(server);
        set({ stale: buildsDiffer(client, server) && !futile });
      })
      .catch(() => undefined)
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    check,
    async beforeConnect() {
      if (!enabled) return true;
      let timer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        check(),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, timeoutMs);
        }),
      ]);
      if (timer !== undefined) clearTimeout(timer);
      if (!state.stale) return true;
      set({ blocking: true });
      return false;
    },
    holdPrompt() {
      holds++;
      set({ held: true });
      let released = false;
      return () => {
        if (released) return;
        released = true;
        holds = Math.max(0, holds - 1);
        set({ held: holds > 0 });
      };
    },
    markReloading() {
      if (lastServer === null) return;
      try {
        storage?.setItem(RELOADED_FOR_KEY, buildKey(lastServer));
      } catch {
        // Storage blocked: the prompt may come back after the reload; nothing worse.
      }
    },
    start() {
      if (!enabled) return () => undefined;
      void check();
      const id = every(() => void check(), intervalMs);
      const onVisible = () => {
        if (doc?.visibilityState === 'visible') void check();
      };
      doc?.addEventListener('visibilitychange', onVisible);
      return () => {
        stopEvery(id);
        doc?.removeEventListener('visibilitychange', onVisible);
      };
    },
  };
}

/** The page's watch (one per tab). */
export const versionWatch: VersionWatch = createVersionWatch();

/** Thrown by a connector whose create / join `beforeConnect` refused (the prompt blocks). */
export class NewVersionError extends Error {
  constructor() {
    super('A new version of Slimez is ready: reload the page.');
    this.name = 'NewVersionError';
  }
}

/** Connectors call this before a create / join: throws `NewVersionError` on a stale client. */
export async function assertCurrentVersion(watch: VersionWatch = versionWatch): Promise<void> {
  if (!(await watch.beforeConnect())) throw new NewVersionError();
}
